import { useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { ProgressDots } from '@/components/shared/progressDots';
import { ImageWithCrops } from '@/services/platform/api/model';
import { bookSevenRoomsReservation } from '@/services/platform/data/booking/sevenrooms';
import { formatDate, formatTime, ImageMap, timePartIconFromTime } from '@/lib/core/utils';
import { useClientTranslation } from '@/lib/core/i18n';
import { BookingDetailBanner } from '@/components/shared/booking/common/detail/banner';
import { LogoLoading } from '@/components/shared/loadings/logo';
import type { ParkingDetailsProps } from './types';
import { useLocaleStore } from '@/lib/core';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

// ── ParkingDetails ────────────────────────────────────────────────────────────

export function ParkingDetails({
  cmsProps,
  booking,
  onBookingChange,
  onBack,
  onSuccess,
  step,
}: ParkingDetailsProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(s => s.locale);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingProgress, setBookingProgress] = useState(0);

  const venueImage = ImageMap(booking.venue?.image as ImageWithCrops, 'bookingDetailHero');
  const venueLogo = ImageMap(booking.venue?.logo as ImageWithCrops);

  const dateDisplay = booking.date ? formatDate(booking.date?.toDateString(), locale) : '';
  const timeDisplay = booking.time ? formatTime(booking.time) : '';
  const timeIcon = timePartIconFromTime(booking.time);

  const venueDisplay = booking.venue?.name ?? '';
  const guestDisplay =
    booking.guestCount !== null ? `${booking.guestCount} ${t('App.Labels.ParkingSpaces')}` : '';

  const dateStr = booking.date
    ? `${booking.date.getFullYear()}-${String(booking.date.getMonth() + 1).padStart(2, '0')}-${String(booking.date.getDate()).padStart(2, '0')}`
    : undefined;

  const noteMaxLength = 2000;

  const handleBook = async () => {
    setIsLoading(true);
    setBookingProgress(0);

    let current = 0;
    const interval = setInterval(() => {
      current += (0.85 - current) * 0.1;
      setBookingProgress(current);
    }, 80);

    let reservation;
    try {
      const groupNotesStr = Object.entries(booking.groupNotes)
        .filter(([, note]) => note.trim())
        .map(([group, note]) => `${group}:\n${note}`)
        .join('\n\n');
      const allNotes = [booking.notes, groupNotesStr].filter(Boolean).join('\n\n');

      reservation = await bookSevenRoomsReservation(
        {
          venueNodeKey: booking?.venue?.venueNodeKey,
          seatingAreaPublicGuid: booking.seatingAreaPublicGuid,
          date: dateStr,
          time: booking.time ?? undefined,
          guestCount: booking.guestCount ?? undefined,
          comments: allNotes || undefined,
          selectedTags: booking.selectedTags.length > 0 ? booking.selectedTags : null,
          selectedGroupTags: booking.selectedGroupTags.length > 0 ? booking.selectedGroupTags : null,
        },
        booking.idempotencyKey
      );
    } finally {
      clearInterval(interval);
    }

    if (!reservation) {
      setIsLoading(false);
      throw new Error('Booking failed');
    }

    setBookingProgress(1);
    await new Promise(r => setTimeout(r, 400));
    onSuccess?.(reservation);
  };

  if (isLoading) {
    return <LogoLoading progress={bookingProgress} className="bg-white" />;
  }

  return (
    <div className="relative flex h-full flex-col bg-white">
      {/* Header — fixed overlay on top of banner */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-99 -mt-[max(1rem,env(safe-area-inset-top))]!">
        <div className="pt-safe-area pointer-events-auto flex items-center justify-between px-4 py-2">
          <button
            type="button"
            aria-label={t('App.Labels.GoBack')}
            className="flex size-11 items-center justify-center"
            onClick={onBack}
          >
            <Icon name="long-arrow-up-left" width={24} height={24} strokeWidth={2} className="text-white" />
          </button>
          <div className="flex flex-1 items-center justify-end">
            <ProgressDots step={step} light />
          </div>
        </div>
      </div>

      {/* Scrollable area — banner + content scroll together */}
      <div className="flex-1">
        {/* Banner */}
        <BookingDetailBanner
          image={venueImage}
          logo={venueLogo}
          rows={[
            [
              ...(dateDisplay ? [{ text: dateDisplay }] : []),
              ...(timeDisplay ? [{ icon: timeIcon, text: timeDisplay }] : []),
            ],
            booking.service
              ? [...(booking.service.name ? [{ icon: 'spark', text: booking.service.name ?? '' }] : [])]
              : [],
            [
              ...(venueDisplay ? [{ icon: 'planimetry', text: venueDisplay }] : []),
              ...(guestDisplay ? [{ icon: 'parking', text: guestDisplay }] : []),
            ],
          ]}
          style={{
            marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top))',
            paddingTop: 'calc(112px + max(1rem, env(safe-area-inset-top))',
          }}
        />

        {/* Expandable Sections */}
        <div className="px-4 pt-10 pb-10">
          {/* Personalize your booking */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="personalize">
              <AccordionTrigger>
                {cmsProps?.personalizeYourBookingLabel ?? t('App.Labels.PersonalizeYourBooking')}
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="flex flex-col gap-1">
                  <div className="h-20 border border-[#dfdfdf] px-3.75 py-2">
                    <textarea
                      className="font-pp-neue-montreal h-full w-full resize-none bg-transparent text-[14px] tracking-[0.5px] text-[#1a1a1a] placeholder:text-[#a9a9a9] focus:outline-none"
                      placeholder={t('App.Labels.BookingNotesPlaceholder')}
                      maxLength={noteMaxLength}
                      value={booking.notes}
                      onChange={e => onBookingChange({ notes: e.target.value })}
                    />
                  </div>
                  <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#666]">
                    {booking.notes.length}/{noteMaxLength}
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
        <Button
          variant="primary-solid"
          palette="dark"
          size="lg"
          className="w-full"
          disabled={isLoading}
          onClick={handleBook}
        >
          {cmsProps?.bookNowLabel ?? t('App.Labels.BookNow')}
        </Button>
      </div>
    </div>
  );
}
